import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BrandColor } from '@styleseat/brand';
import store from '../../../../store';
import type { IRootDispatch, RootState } from '../../../../store/models';
import type { ProSpecialty } from '../../../../modules/Api.types';
import { Stack } from '../../../ui';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import ProPersonalInfoCard from './ProPersonalInfoCard';
import ProBusinessInfoCard from './ProBusinessInfoCard';
import { PublicProvider } from '../../../../api/Providers';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';

type AboutTabProps = {
  provider: PublicProvider | ClientProviderProfile;
  businessHours: React.ReactNode;
  cancellationPolicy?: React.ReactNode;
  depositPolicy?: React.ReactNode;
  contactActions?: React.ReactNode;
  aboutMe?: React.ReactNode;
  locationAction?: React.ReactNode;
  alwaysShowBusinessInfoCard?: boolean;
  onExpandHighlight: (event: { title: string; index: number }) => void;
  onCollapseHighlight: (event: { title: string; index: number }) => void;
  onPressMap: () => void;
  showMap?: boolean;
  shouldShowSpecialties: boolean;
};

export const AboutTab: React.FCWithChildren<AboutTabProps> = ({
  provider,
  businessHours,
  cancellationPolicy,
  depositPolicy,
  contactActions,
  aboutMe,
  locationAction,
  alwaysShowBusinessInfoCard,
  onExpandHighlight,
  onCollapseHighlight,
  onPressMap,
  showMap,
  shouldShowSpecialties,
}) => {
  const { id: providerId } = provider;
  const isMobile = useIsMobile();
  const dispatch = useDispatch<IRootDispatch>();
  const specialties = useSelector<RootState, ProSpecialty[]>(
    store.select.proProfile.getSpecialties({ providerId }),
  );
  // TODO: Remove hard-coded value of false here once we're ready to
  //  show clients the Business Location Instructions on the Pro profile
  const showLocationInstructions = false;

  useEffect(() => {
    if (shouldShowSpecialties) {
      dispatch.proProfile.aboutTabLand({ providerId });
    }
  }, [
    dispatch.proProfile,
    providerId,
    shouldShowSpecialties,
  ]);

  return (
    <Stack color={BrandColor.SuperLight} padding={isMobile ? 12 : 0} space={8}>
      <ProPersonalInfoCard
        provider={provider}
        aboutMe={aboutMe}
        specialties={specialties}
        onExpandHighlight={onExpandHighlight}
        onCollapseHighlight={onCollapseHighlight}
      />
      {(isMobile || alwaysShowBusinessInfoCard) && (
        <ProBusinessInfoCard
          provider={provider}
          businessHours={businessHours}
          cancellationPolicy={cancellationPolicy}
          depositPolicy={depositPolicy}
          onPressMap={onPressMap}
          contactActions={contactActions}
          showMap={showMap}
          showLocationInstructions={showLocationInstructions}
          locationAction={locationAction}
        />
      )}
    </Stack>
  );
};

AboutTab.defaultProps = {
  showMap: true,
};

export default AboutTab;
